import FailureScreen from 'components/appCallback/statusView/failureScreen/FailureScreen';
import FinalPendingScreen from 'components/appCallback/statusView/finalPendingScreen/FinalPendingScreen';
import PendingScreen from 'components/appCallback/statusView/pendingScreen/PendingScreen';
import SuccessScreen from 'components/appCallback/statusView/successScreen/SuccessScreen';
import BlikPending from 'components/blikPending/BlikPending';
import ErrorView from 'components/errorViewSelector/errorView/ErrorView';
import { IErrorProps } from 'components/errorViewSelector/ErrorViewSelector';
import PaymentExpiredScreen from 'components/errorViewSelector/paymentExpiredScreen/PaymentExpiredScreen';
import {
  APP_STATUS,
  AppState,
  BLIK_COUNTDOWN_TIME,
  COUNTDOWN_REQUEST_LOOP_TIME,
  DEFAULT_COUNTDOWN_TIME,
  ERROR_SCREEN_TYPES,
  FAST_COMPLETE,
  REDIRECT_TO_STATUS,
} from 'config/config';
import { PaymentCategoryType } from 'consts';
import getRouteWithParams from 'helpers/getRouteWithParams';
import withLocaleContext from 'hoc/withLocaleContext';
import queryString from 'query-string';
import React, { Component } from 'react';
import { getPaymentDetails, getPaymentStatus } from 'services/Api';
import License from 'types/License';
import { IPayment } from 'types/Payment';

import { IAppState } from 'types/ApplicationState';
import withLicenseNoteContext from '../../hoc/withLicenseNoteContext';
import AbandonedScreen from '../abandonedScreen/AbandonedScreen';

import { IAppCallbackProps, IAppCallbackState } from './AppCallback.types';
import { getExistingPaymentStatus } from './getExistingPaymentStatus/getExistingPaymentStatus';
import withAppStateContext from '../../hoc/withAppStateContext';

const statusArr = [APP_STATUS.NEW, APP_STATUS.PENDING];
const ERROR_REJECT_RECOVERY_ENABLED = 'errorRejectRecoveryEnabled';

type LocationState = {
  status;
};

class AppCallback extends Component<
  IAppCallbackProps & Partial<ILicenseNoteContext> & Partial<ILocaleContext> & Partial<IAppState>,
  IAppCallbackState
> {
  countdown = 0;

  constructor(props) {
    super(props);
    const { location } = this.props;
    this.state = {
      fastRedirect: false,
      isLoading: true,
      paymentDetails: {},
      redirectUrl: '',
      status: location.state && (location.state as LocationState).status,
    };
  }

  async componentDidMount() {
    this.countdown = Date.now();
    try {
      const {
        match: {
          params: { paymentId },
        },
        setPaymentDetails,
        setLicense,
        setLocaleFromPaymentRequest,
        setAppState,
      } = this.props;
      const { status: historyStatus } = this.state;
      let paymentStatus: string = await getExistingPaymentStatus(paymentId);

      setAppState?.(AppState.CALLBACK);

      if (historyStatus && statusArr.indexOf(paymentStatus) !== -1) {
        paymentStatus = historyStatus;
      }

      if (paymentStatus === APP_STATUS.NEW) {
        window.location.assign(window.location.href.replace('/callback', ''));
      }

      const paymentDetails: IPayment = await getPaymentDetails(paymentId);

      if (setLicense) {
        if (paymentDetails.license) {
          setLicense(paymentDetails.license);
        } else {
          setLicense(License.MBANK);
        }
      }

      if (setLocaleFromPaymentRequest) {
        setLocaleFromPaymentRequest(paymentDetails.locale);
      }

      const customQueryParams = { paymentId, paymentStatus };
      const redirectUrl = getRouteWithParams(paymentDetails.continueUrl, customQueryParams);
      if (paymentStatus === APP_STATUS.PENDING) {
        const maxCountdownTime = this.getMaxCountdownTime(paymentDetails.selectedPaymentMethodType);
        this.setRequestInterval(paymentId, maxCountdownTime);

        setTimeout(() => {
          const { status: currentStatus } = this.state;
          if (currentStatus === APP_STATUS.PENDING && Date.now() - this.countdown >= maxCountdownTime) {
            this.setState({
              status: APP_STATUS.FINAL_PENDING,
              redirectUrl: this.getRedirectUrl(currentStatus),
            });
          }
        }, maxCountdownTime);
      }
      setPaymentDetails(paymentDetails, paymentId);
      this.setState({ status: paymentStatus, isLoading: false, paymentDetails, redirectUrl });
    } catch (error) {
      this.setState(() => {
        const castedError: IErrorProps = error as IErrorProps;
        castedError.screenType = this.handleErrorScreenType(castedError.message, castedError.screenType);
        throw castedError;
      });
    }
  }

  getMaxCountdownTime = (paymentMethod: string) =>
    paymentMethod === PaymentCategoryType.BLIK ? BLIK_COUNTDOWN_TIME : DEFAULT_COUNTDOWN_TIME;

  getRedirectUrl = (status: string) => {
    const { redirectUrl } = this.state;
    const { paymentStatus = '' } = queryString.parse(redirectUrl);
    return redirectUrl.replace(paymentStatus as string, status);
  };

  setRequestInterval = (paymentId: string, maxCountdownTime: number) => {
    getPaymentStatus(paymentId)
      .then(({ status }) => {
        if (Date.now() - maxCountdownTime <= this.countdown) {
          // within 6000ms
          if (statusArr.indexOf(status) === -1) {
            if (Date.now() - this.countdown < FAST_COMPLETE) {
              setTimeout(() => {
                this.setState({ status, redirectUrl: this.getRedirectUrl(status), fastRedirect: true });
              }, REDIRECT_TO_STATUS);
            } else {
              this.setState({ status, redirectUrl: this.getRedirectUrl(status) });
            }
          } else {
            // status has not changed
            setTimeout(() => {
              this.setRequestInterval(paymentId, maxCountdownTime);
            }, COUNTDOWN_REQUEST_LOOP_TIME);
          }
        }
      })
      .catch(error => {
        this.setState(() => {
          const castedError: IErrorProps = error as IErrorProps;
          castedError.screenType = this.handleErrorScreenType(castedError.message, castedError.screenType);
          throw castedError;
        });
      });
  };

  handleErrorScreenType = (errorType?: string, defaultView?: string) => {
    switch (errorType) {
      case 'TOKEN_EXPIRED': {
        return ERROR_SCREEN_TYPES.STATUS_TOKEN_EXPIRED;
      }
      default: {
        return defaultView || ERROR_SCREEN_TYPES.PAYMENT;
      }
    }
  };

  statusToView = () => {
    const {
      match: {
        params: { paymentId },
      },
    } = this.props;
    const { status, redirectUrl, fastRedirect, paymentDetails } = this.state;
    const { selectedPaymentMethodType } = queryString.parse(window.location.search);

    if (selectedPaymentMethodType === PaymentCategoryType.BLIK && status === APP_STATUS.PENDING) {
      return <BlikPending />;
    }

    const statuses = {
      [APP_STATUS.SUCCESS]: <SuccessScreen redirect={redirectUrl} />,
      [APP_STATUS.ABANDONED]: <AbandonedScreen paymentId={paymentId} redirectToShop={redirectUrl} />,
      [APP_STATUS.PENDING]: <PendingScreen fastRedirect={fastRedirect} />,
      [APP_STATUS.FINAL_PENDING]: <FinalPendingScreen redirect={redirectUrl} />,
      [APP_STATUS.EXISTS]: <FinalPendingScreen redirect={redirectUrl} />,
      [APP_STATUS.FAILURE]: (
        <FailureScreen
          paymentId={paymentId}
          recoveryEnabled={paymentDetails[ERROR_REJECT_RECOVERY_ENABLED]}
          redirectToShop={redirectUrl}
          status={APP_STATUS.FAILURE}
        />
      ),
      [APP_STATUS.WAITING_FOR_SUCCESS]: <SuccessScreen redirect={redirectUrl} />,
      [APP_STATUS.ERROR]: (
        <FailureScreen
          paymentId={paymentId}
          recoveryEnabled={paymentDetails[ERROR_REJECT_RECOVERY_ENABLED]}
          redirectToShop={redirectUrl}
          status={APP_STATUS.ERROR}
        />
      ),
      [APP_STATUS.EXPIRED]: <PaymentExpiredScreen />,
    };

    return statuses[status] || <ErrorView />;
  };

  render() {
    const { isLoading, fastRedirect } = this.state;

    if (isLoading) {
      return false;
    }

    if (fastRedirect) {
      setTimeout(() => {
        this.setState({ fastRedirect: false });
      }, REDIRECT_TO_STATUS);
      return <PendingScreen fastRedirect={fastRedirect} />;
    }

    return this.statusToView();
  }
}

export default withLicenseNoteContext(withLocaleContext(withAppStateContext(AppCallback)));
